import { useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getWallet, listRecharges, requestRecharge } from '../../api/portal'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { DataState } from '../../components/DataState'
import { QueryError } from '../../components/QueryError'
import { EmptyState } from '../../components/ui/EmptyState'
import { Fact } from '../../components/ui/Fact'
import { Ltr } from '../../components/ui/Ltr'
import { MoneyInput } from '../../components/ui/NumericField'
import { PageHeader } from '../../components/ui/PageHeader'
import { SectionCard } from '../../components/ui/SectionCard'
import { StatusChip } from '../../components/ui/StatusChip'
import { formatDateTime, formatIrr } from '../../format'
import { toInteger } from '../../numerals'

/** Amounts an operator actually credits, so most customers never type at all. */
const SUGGESTIONS = [5_000_000, 10_000_000, 20_000_000, 50_000_000]

/**
 * Asking us to add credit.
 *
 * There is no payment gateway on this platform, and this page does not pretend
 * otherwise. What it produces is a *request*: a record with a reference the
 * customer can quote and a status they can watch. The balance moves when
 * somebody authorised settles it, which is a separate act by a different
 * person — so nothing here says «پرداخت موفق», and nothing here writes to the
 * ledger.
 *
 * Saying that plainly is the whole design. A page that took an amount and then
 * showed a spinner would be inviting the customer to believe they had paid.
 */
export function RechargePage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [submitted, setSubmitted] = useState<string | null>(null)

  const mayAsk = can(membership, 'request_recharge')
  const rials = toInteger(amount)

  // This is the screen somebody sits on while they wait for an operator to
  // settle. Both queries revalidate when the tab comes back, so returning to
  // it after a phone call shows the settled status and the new balance
  // without a reload — and neither polls, because nothing here changes on its
  // own between visits.
  const wallet = useQuery({
    queryKey: ['client', 'wallet', customerId],
    queryFn: ({ signal }) => getWallet(customerId, signal),
    enabled: can(membership, 'view_wallet'),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    staleTime: 15_000,
  })
  const history = useQuery({
    queryKey: ['client', 'recharges', customerId],
    queryFn: ({ signal }) => listRecharges(customerId, signal),
    enabled: can(membership, 'view_wallet'),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    staleTime: 15_000,
  })

  const ask = useMutation({
    mutationFn: () => requestRecharge(customerId, rials ?? 0, ''),
    onSuccess: (created) => {
      setSubmitted(created.reference)
      setAmount('')
      // The new request must appear in the history below immediately, not on
      // the next visit: a customer who submits and sees nothing change assumes
      // it did not work and submits again.
      void queryClient.invalidateQueries({ queryKey: ['client', 'recharges', customerId] })
    },
  })

  const rows = Array.isArray(history.data) ? history.data : []

  return (
    <Box>
      <PageHeader
        title="افزایش اعتبار"
        description="درخواست افزایش اعتبار خود را اینجا ثبت کنید. پس از بررسی و دریافت وجه، مبلغ به موجودی شما افزوده می‌شود."
      />

      <Stack spacing={3}>
        <SectionCard>
          <Grid container spacing={3} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" component="p" color="text.secondary">
                موجودی قابل استفاده
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(1.5rem, 3.2vw, 2.25rem)',
                  lineHeight: 1.3,
                }}
              >
                {wallet.data ? formatIrr(wallet.data.available_balance_irr) : '—'}
              </Typography>
            </Grid>
          </Grid>
        </SectionCard>

        {submitted ? (
          <Alert severity="success" onClose={() => setSubmitted(null)}>
            <AlertTitle>درخواست شما ثبت شد</AlertTitle>
            {/* Not «پرداخت شد». Nothing has been paid and nothing has moved. */}
            شمارهٔ پیگیری: <Ltr mono>{submitted}</Ltr> — پس از دریافت و تأیید وجه، مبلغ به موجودی
            شما افزوده می‌شود و در همین صفحه دیده خواهد شد.
          </Alert>
        ) : null}

        {mayAsk ? (
          <SectionCard
            title="مبلغ افزایش اعتبار"
            description="مبلغ را به ریال وارد کنید یا یکی از مبالغ پیشنهادی را انتخاب کنید."
          >
            <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {SUGGESTIONS.map((value) => (
                  <Chip
                    key={value}
                    label={formatIrr(value)}
                    variant={rials === value ? 'filled' : 'outlined'}
                    color={rials === value ? 'primary' : 'default'}
                    onClick={() => setAmount(String(value))}
                  />
                ))}
              </Stack>

              <MoneyInput
                label="مبلغ افزایش اعتبار"
                value={amount}
                onChange={setAmount}
                fullWidth
                helperText="مبلغ به ریال و عدد صحیح."
              />

              {rials !== null && rials > 0 ? (
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Fact label="مبلغ درخواستی">{formatIrr(rials)}</Fact>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    موجودی پس از تأیید: {formatIrr((wallet.data?.available_balance_irr ?? 0) + rials)}
                  </Typography>
                </Box>
              ) : null}

              {ask.isError ? <QueryError error={ask.error} /> : null}

              <Box>
                <Button
                  variant="contained"
                  size="large"
                  disabled={rials === null || rials <= 0 || ask.isPending}
                  onClick={() => ask.mutate()}
                >
                  {ask.isPending ? 'در حال ثبت…' : 'ثبت درخواست افزایش اعتبار'}
                </Button>
              </Box>

              {/* Honest about the current model. A customer who expects an
                  online payment page and does not get one will assume the page
                  is broken; one who is told will send the money. */}
              <Alert severity="info">
                پرداخت آنلاین هنوز در این سامانه فعال نیست. پس از ثبت، شمارهٔ پیگیری به شما داده
                می‌شود؛ برای هماهنگی واریز با همان شماره با پشتیبانی تماس بگیرید.
              </Alert>
            </Stack>
          </SectionCard>
        ) : (
          <Alert severity="info">
            ثبت درخواست افزایش اعتبار در نقش شما نیست. مالک یا مسئول مالی حساب می‌تواند آن را ثبت
            کند.
          </Alert>
        )}

        <SectionCard title="درخواست‌های افزایش اعتبار">
          <DataState
            isPending={history.isPending}
            isError={history.isError}
            error={history.error}
            onRetry={() => void history.refetch()}
          >
            {rows.length === 0 ? (
              <EmptyState
                title="هنوز درخواستی برای افزایش اعتبار ثبت نکرده‌اید"
                hint="پس از ثبت، وضعیت هر درخواست همین‌جا دنبال می‌شود."
              />
            ) : (
              <Stack divider={<Divider />}>
                {rows.map((row) => (
                  <Stack
                    key={row.id}
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center', py: 1.5, minWidth: 0 }}
                  >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatIrr(row.amount_irr)}
                      </Typography>
                      <Ltr mono variant="caption" color="text.secondary">
                        {row.reference}
                      </Ltr>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }}
                    >
                      {formatDateTime(row.created_at)}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                      <StatusChip status={row.status} />
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </DataState>
        </SectionCard>
      </Stack>
    </Box>
  )
}
